import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFileText, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import Header from './Header';
import Footer from './Footer';

const Main = styled.main`
  min-height: 100vh;
  width: 100%;
  padding-top: 80px;
`;

// Navigation par sections
const SectionNav = styled(motion.div)`
  position: fixed;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 9999;
  
  @media (max-width: 768px) {
    right: 0.5rem;
    gap: 0.3rem;
  }
  
  @media (max-width: 480px) {
    right: 0.5rem;
  }
`;

const NavArrow = styled(motion.button)`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: var(--white);
  color: var(--dark);
  border: 2px solid var(--gray-200);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  body.dark-mode & {
    background: var(--dark);
    color: var(--white);
    border-color: var(--gray-700);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }
  
  &:hover {
    background: var(--primary);
    color: var(--white);
    border-color: var(--primary);
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    &:hover {
      transform: none;
      background: var(--white);
      color: var(--dark);
      border-color: var(--gray-200);
      
      body.dark-mode & {
        background: var(--dark);
        color: var(--white);
        border-color: var(--gray-700);
      }
    }
  }
  
  svg {
    font-size: 1.5rem;
  }
  
  @media (max-width: 768px) {
    width: 38px;
    height: 38px;
    
    svg {
      font-size: 1.2rem;
    }
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    border-width: 1px;
    
    svg {
      font-size: 1rem;
    }
  }
`;

const SectionDots = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0;
  
  @media (max-width: 480px) {
    display: none;
  }
`;

const SectionDot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? 'var(--secondary)' : 'var(--gray-400)'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  
  body.dark-mode & {
    background: ${props => props.$active ? 'var(--secondary)' : 'var(--gray-600)'};
  }
  
  &:hover {
    background: var(--secondary);
    transform: scale(1.3);
  }
`;

const FloatingButton = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--secondary) 0%, #e6b800 100%);
  color: var(--black);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(255, 206, 0, 0.4);
  z-index: 999;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    bottom: 1.5rem;
    right: 4rem;
    width: 50px;
    height: 50px;
  }
  
  @media (max-width: 480px) {
    bottom: 1rem;
    right: 3rem;
    width: 45px;
    height: 45px;
    
    svg {
      font-size: 1.2rem;
    }
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(255, 206, 0, 0.5);
  }
  
  svg {
    font-size: 1.5rem;
  }
`;

const FloatingLabel = styled(motion.span)`
  position: fixed;
  bottom: 2.5rem;
  right: 5.5rem;
  background-color: var(--dark);
  color: var(--white);
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-sm);
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  z-index: 998;
  
  &::after {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    border-left: 6px solid var(--dark);
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
  
  .dark-mode & {
    background-color: var(--white);
    color: var(--dark);
    
    &::after {
      border-left-color: var(--white);
    }
  }
`;

const Layout = ({ children }) => {
  const [showFloating, setShowFloating] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  
  // Liste des sections dans l'ordre
  const sections = ['accueil', 'services', 'realisations', 'a-propos', 'contact', 'footer'];
  
  useEffect(() => {
    const handleScroll = () => {
      // Afficher le bouton flottant après avoir scrollé de 300px
      if (window.scrollY > 300) {
        setShowFloating(true);
      } else {
        setShowFloating(false);
      }
      
      // Déterminer la section actuelle
      // Si on est tout en haut, c'est la section accueil (Hero)
      if (window.scrollY < 50) {
        setCurrentSectionIndex(0);
        return;
      }
      
      // Si on est tout en bas, c'est le footer
      const isAtBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 50;
      if (isAtBottom) {
        setCurrentSectionIndex(sections.length - 1);
        return;
      }
      
      // Trouver la section visible au milieu de l'écran
      const viewportMiddle = window.scrollY + window.innerHeight / 2;
      
      let currentIndex = 0;
      for (let i = 0; i < sections.length; i++) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionBottom = sectionTop + section.offsetHeight;
          
          if (viewportMiddle >= sectionTop && viewportMiddle < sectionBottom) {
            currentIndex = i;
            break;
          } else if (viewportMiddle >= sectionTop) {
            currentIndex = i;
          }
        }
      }
      
      setCurrentSectionIndex(currentIndex);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Appel initial
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);
  
  const scrollToDevis = () => {
    const devisSection = document.getElementById('devis');
    if (devisSection) {
      devisSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const goToPrevSection = () => {
    if (currentSectionIndex > 0) {
      const prevSection = sections[currentSectionIndex - 1];
      scrollToSection(prevSection);
    } else if (currentSectionIndex === 0) {
      // Si déjà sur accueil, s'assurer qu'on est bien en haut
      const section = document.getElementById('accueil');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  const goToNextSection = () => {
    if (currentSectionIndex < sections.length - 1) {
      scrollToSection(sections[currentSectionIndex + 1]);
    }
  };
  
  return (
    <>
      <Header />
      <Main>{children}</Main>
      <Footer />
      
      {/* Navigation par sections */}
      <SectionNav>
        <NavArrow 
          onClick={goToPrevSection}
          aria-label="Section précédente"
          whileTap={{ scale: 0.9 }}
        >
          <FiChevronUp />
        </NavArrow>
        
        <SectionDots>
          {sections.map((section, index) => (
            <SectionDot
              key={section}
              $active={index === currentSectionIndex}
              onClick={() => scrollToSection(section)}
              aria-label={`Aller à ${section}`}
            />
          ))}
        </SectionDots>
        
        <NavArrow 
          onClick={goToNextSection}
          aria-label="Section suivante"
          whileTap={{ scale: 0.9 }}
        >
          <FiChevronDown />
        </NavArrow>
      </SectionNav>

      <AnimatePresence>
        {showFloating && (
          <>
            <FloatingButton
              onClick={scrollToDevis}
              onMouseEnter={() => setShowLabel(true)}
              onMouseLeave={() => setShowLabel(false)}
              initial={{ opacity: 0, scale: 0, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              aria-label="Estimez votre projet"
            >
              <FiFileText />
            </FloatingButton>
            
            <AnimatePresence>
              {showLabel && (
                <FloatingLabel
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  Devis gratuit
                </FloatingLabel>
              )}
            </AnimatePresence>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Layout;
